<template>
  <div>
    <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
      <h2
        v-if="!embedded"
        class="mt-0 mb-0 tw-text-2xl tw-font-semibold"
        style="color: var(--text-color)"
      >
        Waiting for RSS Redirect
      </h2>
      <Button
        icon="pi pi-refresh"
        label="Refresh"
        outlined
        size="small"
        :loading="loading"
        @click="fetchActions"
      />
    </div>
    <p class="tw-text-sm tw-mb-4" style="color: var(--text-color-secondary)">
      Campaigns of these jamx network shows start automatically once the
      original RSS redirects to the show's RSS. Checked once a day.
    </p>

    <DataTable
      :value="actions"
      :loading="loading"
      :paginator="actions.length > 20"
      :rows="20"
      dataKey="id"
      responsiveLayout="scroll"
      sortField="created_at"
      :sortOrder="1"
    >
      <template #empty>
        <div class="tw-text-center tw-py-6">
          No shows waiting for a redirect
        </div>
      </template>

      <Column field="show_title" header="Show" sortable>
        <template #body="{ data }">
          <div class="tw-font-medium">
            {{ showLabel(data) }}
          </div>
          <div class="tw-text-xs" style="color: var(--text-color-secondary)">
            {{ data.hosted }}
          </div>
        </template>
      </Column>

      <Column field="podcaster_name" header="Podcaster" sortable>
        <template #body="{ data }">
          <div>
            {{
              data.existing_podcaster_id
                ? data.podcaster_name
                : "podcaster deleted"
            }}
          </div>
          <div class="tw-text-xs" style="color: var(--text-color-secondary)">
            {{ data.email }}
          </div>
        </template>
      </Column>

      <Column header="Original RSS → Show RSS">
        <template #body="{ data }">
          <div class="tw-text-xs tw-break-all">{{ data.original_rss_url }}</div>
          <div class="tw-text-xs tw-break-all tw-mt-1">
            → {{ data.rss_url || "-" }}
          </div>
        </template>
      </Column>

      <Column header="Campaigns">
        <template #body="{ data }">
          <div
            v-for="campaign in data.campaigns"
            :key="campaign.type"
            class="tw-text-xs"
          >
            {{ campaign.type }} · {{ campaign.interval }}
          </div>
        </template>
      </Column>

      <Column field="created_at" header="Waiting" sortable>
        <template #body="{ data }">{{ daysSince(data.created_at) }} d</template>
      </Column>

      <Column field="status" header="Status" sortable>
        <template #body="{ data }">
          <Tag :value="data.status" :severity="statusSeverity(data.status)" />
          <div
            v-if="data.last_checked_at"
            class="tw-text-xs tw-mt-1"
            style="color: var(--text-color-secondary)"
          >
            {{ formatDateTime(data.last_checked_at) }}
          </div>
        </template>
      </Column>

      <Column header="Actions">
        <template #body="{ data }">
          <span
            v-if="data.status === 'activation_failed'"
            class="tw-text-xs tw-text-red-500"
          >
            Create campaigns manually
          </span>
          <div v-else class="tw-flex tw-gap-2">
            <Button
              label="Check"
              size="small"
              outlined
              :loading="checkingId === data.id"
              :disabled="!!checkingId"
              @click="checkAction(data)"
            />
            <Button
              label="Cancel"
              size="small"
              severity="secondary"
              outlined
              @click="actionToCancel = data"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog
      :visible="!!checkResult"
      header="Redirect check"
      modal
      :style="{ width: '700px', maxWidth: '90vw' }"
      @update:visible="checkResult = null"
    >
      <template v-if="checkResult">
        <div class="tw-mb-3">
          <Tag
            :value="checkResult.status"
            :severity="statusSeverity(checkResult.status)"
          />
          <span v-if="checkResult.startedCampaigns" class="tw-ml-2">
            {{ checkResult.startedCampaigns }} campaign(s) started
          </span>
        </div>
        <div
          v-for="(step, index) in checkResult.chain"
          :key="index"
          class="tw-text-xs tw-break-all"
        >
          {{ step.status || step.error || "→" }} {{ step.url }}
        </div>
        <div v-if="checkResult.newFeedUrl" class="tw-text-xs tw-break-all">
          itunes:new-feed-url: {{ checkResult.newFeedUrl }}
        </div>
      </template>
    </Dialog>

    <Dialog
      :visible="!!actionToCancel"
      header="Stop waiting for the redirect"
      modal
      :style="{ width: '450px' }"
      @update:visible="actionToCancel = null"
    >
      <p v-if="actionToCancel">
        "{{ showLabel(actionToCancel) }}" will no longer be checked and its
        campaigns will not start automatically. Create them manually if needed.
      </p>
      <template #footer>
        <Button label="Back" outlined @click="actionToCancel = null" />
        <Button
          label="Stop waiting"
          severity="danger"
          :loading="cancelling"
          @click="cancelAction"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import axios from "axios";
import { useToast } from "primevue/usetoast";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Tag from "primevue/tag";
import { API_BASE_URL } from "@/consts";

// eslint-disable-next-line no-undef
defineProps({
  // Rendered as a tab of the Shows page: the tab header replaces the title
  embedded: { type: Boolean, default: false },
});
// eslint-disable-next-line no-undef
const emit = defineEmits(["count"]);

const toast = useToast();
const token = localStorage.getItem("podcastAdminToken");
const headers = { Authorization: `Bearer ${token}` };
const baseUrl = `${API_BASE_URL}/shows-delayed-actions`;

const actions = ref([]);
const loading = ref(false);
const checkingId = ref(null);
const checkResult = ref(null);
const actionToCancel = ref(null);
const cancelling = ref(false);

const STATUS_SEVERITY = {
  activated: "success",
  waiting: "info",
  not_redirected: "info",
  temp_redirect: "warning",
  check_failed: "warning",
  redirected_elsewhere: "danger",
  same_rss: "danger",
  invalid_original_rss: "danger",
  invalid_new_rss: "danger",
  show_deleted: "danger",
  podcaster_deleted: "danger",
  activation_failed: "danger",
};

const statusSeverity = (status) => STATUS_SEVERITY[status] || "info";

// A new show has no title until podcast_shows_fetch reads its feed
const showLabel = (action) => {
  if (!action.existing_show_id) return "show deleted";
  return action.show_title || "title not fetched yet";
};

const daysSince = (date) =>
  date ? Math.floor((Date.now() - new Date(date).getTime()) / 864e5) : "-";

const formatDateTime = (date) => new Date(date).toLocaleString();

const showError = (error, fallback) =>
  toast.add({
    severity: "error",
    summary: "Error",
    detail: error.response?.data?.message || fallback,
    life: 4000,
  });

const fetchActions = async () => {
  loading.value = true;
  try {
    const response = await axios.get(baseUrl, { headers });
    actions.value = response.data.data || [];
    emit("count", actions.value.length);
  } catch (error) {
    showError(error, "Failed to load shows");
  } finally {
    loading.value = false;
  }
};

const checkAction = async (action) => {
  checkingId.value = action.id;
  try {
    const response = await axios.post(
      `${baseUrl}/${action.id}/check`,
      {},
      { headers }
    );
    checkResult.value = response.data.data;
    await fetchActions();
  } catch (error) {
    showError(error, "Check failed");
  } finally {
    checkingId.value = null;
  }
};

const cancelAction = async () => {
  cancelling.value = true;
  try {
    await axios.post(
      `${baseUrl}/${actionToCancel.value.id}/cancel`,
      {},
      { headers }
    );
    toast.add({
      severity: "success",
      summary: "Stopped",
      detail: "The show is no longer waiting for the redirect",
      life: 3000,
    });
    actionToCancel.value = null;
    await fetchActions();
  } catch (error) {
    showError(error, "Failed to stop waiting");
  } finally {
    cancelling.value = false;
  }
};

onMounted(fetchActions);
</script>
